'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';

/**
 * Represent a book character with an explicit library/story character (hub picker).
 * The picked character's appearance image is used as the style-transfer source when
 * it has one; otherwise a text-only reference is generated from its description. The
 * book reference is (re)linked to the picked character.
 */
export async function applyCharacterToIllustrationReference(input: {
  bookId: string;
  extractedName: string;
  sourceCharacterId: string;
}): Promise<ActionResult<{ imageUrl: string | null; tokensUsed: number | null }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { CharacterReferenceService } = await import('@/contexts/illustrations/services/CharacterReferenceService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const { getTokenManager } = await import('@/contexts/subscriptions/server');

    const service = new CharacterReferenceService(
      getIllustrationRepository(),
      getCharacterRepository(),
      getTokenManager(),
    );

    const result = await service.generateReference({
      bookId: input.bookId,
      familyId,
      extractedName: input.extractedName,
      createdBy: userId,
      forceRegenerate: true,
      sourceCharacterId: input.sourceCharacterId,
    });

    if (!result.success) {
      return { success: false, error: result.error || 'GENERATION_FAILED' };
    }

    return {
      success: true,
      data: {
        imageUrl: result.imageUrl ?? null,
        tokensUsed: result.tokensUsed ?? null,
      },
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('applyCharacterToBookReference', e) };
  }
}
